import { useMemo } from "react";
import {
  getLayerKeyframeInfo,
  getKeyframeRows,
  calculateTrackHeight,
  TRACK_HEIGHT_CONFIG,
  type KeyframeRow,
} from "../utils/keyframeHelpers";
import {
  getLayerData,
  type TimelineLayerInfo,
  type LayerKeyframeData,
} from "../utils/layerHierarchy";
import { applyCustomModsToLayerData } from "../utils/moddedLayerData";
import { useMst } from "../../../stores/main";
import { recordStudioPerf, studioPerfNow } from "../../../helpers/studioPerfDebug";

export interface TrackHeightInfo {
  height: number;
  rows: KeyframeRow[];
  hasKeyframes: boolean; // True if layer has keyframes (regardless of visibility state)
}

/**
 * Hook to compute track heights for all layers based on their keyframe property types
 */
export function useTrackHeights(
  flatLayers: Array<{ layer: TimelineLayerInfo; depth: number }>,
  jsonFile: unknown,
  isLayerKeyframesExpanded: (nodeKey: string) => boolean
): Map<string, TrackHeightInfo> {
  const { modsStore } = useMst();

  return useMemo(() => {
    const startedAt = studioPerfNow();
    const heightMap = new Map<string, TrackHeightInfo>();
    const mods = modsStore.mods?.slice() ?? [];

    for (const { layer } of flatLayers) {
      const layerData = getLayerData(jsonFile, layer.layerPath);
      const resolvedLayerData = applyCustomModsToLayerData(layerData, layer, mods);
      const keyframes = resolvedLayerData ? getLayerKeyframeInfo(resolvedLayerData) : [];
      const rows = getKeyframeRows(keyframes);
      const hasKeyframes = rows.length > 0;

      // Check if keyframes should be visible for this layer
      const showKeyframes = isLayerKeyframesExpanded(layer.nodeKey);

      // If keyframes are hidden, use empty rows and collapsed height
      const effectiveRows = showKeyframes ? rows : [];
      const height = showKeyframes
        ? calculateTrackHeight(rows.length)
        : TRACK_HEIGHT_CONFIG.COLLAPSED_HEIGHT;

      heightMap.set(layer.nodeKey, {
        height,
        rows: effectiveRows,
        hasKeyframes,
      });
    }

    recordStudioPerf("TimelinePanel useTrackHeights", studioPerfNow() - startedAt, {
      layers: flatLayers.length,
      withKeyframes: Array.from(heightMap.values()).filter(
        (entry) => entry.hasKeyframes
      ).length,
    });

    return heightMap;
  }, [flatLayers, jsonFile, isLayerKeyframesExpanded, modsStore.mods]);
}

// Re-export TRACK_HEIGHT_CONFIG for use in other components
export { TRACK_HEIGHT_CONFIG };

/**
 * Hook to compute keyframe data for all layers (without height calculation)
 * Used by the tree flattening function to build property nodes
 */
export function useLayerKeyframeData(
  flatLayers: Array<{ layer: TimelineLayerInfo; depth: number }>,
  jsonFile: unknown
): Map<string, LayerKeyframeData> {
  const { modsStore } = useMst();

  return useMemo(() => {
    const startedAt = studioPerfNow();
    const dataMap = new Map<string, LayerKeyframeData>();
    const mods = modsStore.mods?.slice() ?? [];

    for (const { layer } of flatLayers) {
      const layerData = getLayerData(jsonFile, layer.layerPath);
      const resolvedLayerData = applyCustomModsToLayerData(layerData, layer, mods);
      const keyframes = resolvedLayerData ? getLayerKeyframeInfo(resolvedLayerData) : [];
      const rows = getKeyframeRows(keyframes);
      const hasKeyframes = rows.length > 0;

      dataMap.set(layer.nodeKey, {
        rows,
        hasKeyframes,
      });
    }

    recordStudioPerf(
      "TimelinePanel useLayerKeyframeData",
      studioPerfNow() - startedAt,
      {
        layers: flatLayers.length,
        withKeyframes: Array.from(dataMap.values()).filter(
          (entry) => entry.hasKeyframes
        ).length,
      }
    );

    return dataMap;
  }, [flatLayers, jsonFile, modsStore.mods]);
}
